import { getPanelQueryStrings } from '@chronosphereio/ai';
import {
  Box,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@chronosphereio/chrono-ui';
import { useLocalStorage } from '@chronosphereio/core';
import { Debounce } from '@chronosphereio/shared';
import { Select, SelectProps } from '@mui/material';
import { produce } from 'immer';
import { PageLayoutBody, PageLayoutSidebarRight } from 'mdi-material-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorAlert, ErrorBoundary, TimeRangeControls } from '@/components';
import { PanelDefinition, QueryDefinition } from '@/core';
import { getQueryDefinitionsFromDataType } from '@/dashboards/utils';
import {
  type FieldAIProps,
  getSupportedDataType,
  PanelSpecEditor,
  PluginKindSelect,
  PluginKindSelectProps,
  useAnalytics,
  usePluginEditor,
  usePluginRegistry,
  useVizFrameworkConfig,
  VariableOpportunitiesProvider,
} from '@/plugin-system';
import {
  PanelEditorValues,
  SplitVariable,
  TimeOverrideProvider,
  useListPanelGroups,
  useOptionalTemplateVariableDefinitions,
  useOtherPanelQueries,
  usePanelGroup,
} from '../../context';
import { DisabledTimeRangeControls } from '../DisabledTimeRangeControls';
import { PanelProvider } from '../Panel';
import { FieldWithGenerateButton } from './FieldWithGenerateButton';
import { FieldWithSuggestion } from './FieldWithSuggestion';
import { PanelPreview } from './PanelPreview';
import { usePanelEditor } from './usePanelEditor';

const PANEL_LAYOUT_STORAGE_KEY = 'CHRONOSPHERE_PANEL_LAYOUT';

enum PanelLayoutOptions {
  WIDESCREEN = 'WIDESCREEN',
  NARROW = 'NARROW',
}

export interface PanelEditorFormProps {
  initialValues: PanelEditorValues;
  onChange: (values: PanelEditorValues) => void;
  panelKey?: string;
}

export function PanelEditorForm(props: PanelEditorFormProps) {
  const {
    initialValues: { panelDefinition: initialPanelDef, groupId: initialGroupId },
    onChange,
    panelKey,
  } = props;

  const [panelLayout, setPanelLayout] = useLocalStorage<PanelLayoutOptions>(
    PANEL_LAYOUT_STORAGE_KEY,
    PanelLayoutOptions.WIDESCREEN
  );

  const group = usePanelGroup(initialGroupId);
  const analytics = useAnalytics();
  const panelGroups = useListPanelGroups();
  const gridPanelGroups = useMemo(() => panelGroups.filter((group) => group.kind === 'Grid'), [panelGroups]);
  const [groupId, setGroupId] = useState(initialGroupId);
  const {
    panelDefinition,
    setName,
    setDescription,
    setFormulas,
    setQueries,
    setQueryOptions,
    setPlugin,
    setLinks,
    setPanelDefinition,
  } = usePanelEditor(initialPanelDef);
  const { plugin } = panelDefinition.spec;

  const { getPlugin, defaultPluginKinds } = usePluginRegistry();
  const variableDefinitions = useOptionalTemplateVariableDefinitions();
  const config = useVizFrameworkConfig();
  const enableVariableOpportunities = config?.enableVariableOpportunities ?? false;
  const otherQueries = useOtherPanelQueries(panelKey);

  const { queryStrings, queryTypes } = useMemo(
    () => getPanelQueryStrings(panelDefinition.spec.queries),
    [panelDefinition.spec.queries]
  );

  const PanelNameDescriptionGenerator = config?.PanelNameDescriptionGenerator;

  const handleAIGenerate = useCallback(
    (name: string, description: string) => {
      const currentName = panelDefinition.spec.display.name ?? '';
      const currentDescription = panelDefinition.spec.display.description ?? '';

      if (name !== currentName) {
        setName(name);
      }
      if (description !== currentDescription) {
        setDescription(description);
      }
    },
    [setName, setDescription, panelDefinition.spec.display.name, panelDefinition.spec.display.description]
  );

  // Use common plugin editor logic even though we've split the inputs up in this form
  const pluginEditor = usePluginEditor({
    pluginType: 'Panel',
    value: { kind: plugin.kind, spec: plugin.spec },
    onChange: (plugin) => {
      setPlugin(plugin);
    },
    onHideQueryEditorChange: (isHidden) => {
      setQueries(undefined, isHidden);
    },
  });

  // Ignore string values (which would be an "empty" value from the Select) since we don't allow them to unset it
  const handleGroupChange: SelectProps<number>['onChange'] = (e) => {
    const { value } = e.target;
    if (typeof value === 'string') {
      return;
    }
    setGroupId(value);
  };

  const handlePanelDefinitionChange = (nextPanelDef: PanelDefinition) => {
    const { kind: pluginKind, spec: pluginSpec } = nextPanelDef.spec.plugin;
    // if panel plugin kind and spec are modified, then need to save current spec
    if (
      panelDefinition.spec.plugin.kind !== pluginKind &&
      JSON.stringify(panelDefinition.spec.plugin.spec) !== JSON.stringify(pluginSpec)
    ) {
      pluginEditor.rememberCurrentSpecState();
    }

    setPanelDefinition(nextPanelDef);
  };

  const handlePanelKindChange: PluginKindSelectProps['onChange'] = async (e) => {
    const nextPanelKind = e.target.value;
    const nextPanelPlugin = await getPlugin('Panel', nextPanelKind);
    // Get spec for this panel kind if it was selected previously
    let nextPanelSpec = pluginEditor.getPrevSpecState('Panel', nextPanelKind);
    if (nextPanelSpec === undefined) {
      nextPanelSpec = nextPanelPlugin.createInitialOptions({ config });
    }

    // Update query definitions based on the next panel kind
    const queries = panelDefinition.spec.queries;
    const nextDataType = getSupportedDataType(nextPanelSpec, nextPanelPlugin);
    const nextQueries: QueryDefinition[] = await getQueryDefinitionsFromDataType({
      getPlugin,
      queries,
      dataType: nextDataType,
      defaultDataQueryKind: defaultPluginKinds?.['DataQuery'],
      config,
    });

    const newPanelDefinition = produce(panelDefinition, (draft) => {
      draft.spec.plugin.kind = nextPanelKind;
      draft.spec.plugin.spec = nextPanelSpec ?? nextPanelPlugin.createInitialOptions({ config });
      draft.spec.queries = nextQueries;
    });

    setPanelDefinition(newPanelDefinition);
  };

  useEffect(() => {
    const values: PanelEditorValues = { groupId, panelDefinition };
    onChange(values);
  }, [groupId, panelDefinition, onChange]);

  useEffect(() => {
    analytics?.trackMetric('dashboard-edit_panel_settings', {
      panel_layout: panelLayout,
    });

    // Only record once on mount:
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRelativeTimeOverrideDefined = panelDefinition.spec.query_options?.time?.start !== undefined;

  const renderNameField = (aiProps?: FieldAIProps) => {
    return (
      <Debounce value={panelDefinition.spec.display.name ?? ''} onChange={setName}>
        {(value, setValue) => {
          if (aiProps?.mode === 'suggestion') {
            return (
              <FieldWithSuggestion value={value} onChange={setValue} label="Name" suggestion={aiProps.suggestion} />
            );
          }
          if (aiProps?.mode === 'generate') {
            return (
              <FieldWithGenerateButton value={value} onChange={setValue} label="Name" generate={aiProps.generate} />
            );
          }
          return <TextField fullWidth label="Name" value={value} onChange={(e) => setValue(e.target.value)} />;
        }}
      </Debounce>
    );
  };

  const renderDescriptionField = (aiProps?: FieldAIProps) => {
    return (
      <Debounce value={panelDefinition.spec.display.description ?? ''} onChange={setDescription}>
        {(value, setValue) => {
          if (aiProps?.mode === 'suggestion') {
            return (
              <FieldWithSuggestion
                value={value}
                onChange={setValue}
                label="Description"
                suggestion={aiProps.suggestion}
              />
            );
          }
          if (aiProps?.mode === 'generate') {
            return (
              <FieldWithGenerateButton
                value={value}
                onChange={setValue}
                label="Description"
                generate={aiProps.generate}
              />
            );
          }
          return <TextField fullWidth label="Description" value={value} onChange={(e) => setValue(e.target.value)} />;
        }}
      </Debounce>
    );
  };

  const groupField = (
    <FormControl fullWidth>
      <InputLabel id="select-group">Group</InputLabel>
      <Select required labelId="select-group" label="Group" value={groupId} onChange={handleGroupChange}>
        {gridPanelGroups.map((panelGroup, index) => (
          <MenuItem key={panelGroup.id} value={panelGroup.id}>
            {panelGroup.title ?? `Group ${index + 1}`}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const typeField = (
    <>
      <FormControl fullWidth disabled={pluginEditor.isLoading} error={pluginEditor.error !== null}>
        <InputLabel id="panel-type-label">Type</InputLabel>
        <PluginKindSelect
          pluginType="Panel"
          required
          labelId="panel-type-label"
          label="Type"
          value={pluginEditor.pendingKind ? pluginEditor.pendingKind : plugin.kind}
          onChange={handlePanelKindChange}
        />
      </FormControl>
      <FormHelperText>{pluginEditor.error?.message ?? ''}</FormHelperText>
    </>
  );

  const editor = (
    <ErrorBoundary FallbackComponent={ErrorAlert}>
      <Container sx={{ padding: 0 }}>
        <Container.Body>
          <VariableOpportunitiesProvider
            enabled={enableVariableOpportunities}
            otherQueries={otherQueries}
            variableDefinitions={variableDefinitions}
          >
            <PanelSpecEditor
              panelDefinition={panelDefinition}
              onJSONChange={handlePanelDefinitionChange}
              onFormulasChange={(formulas) => {
                setFormulas(formulas);
              }}
              onQueriesChange={(queries) => {
                setQueries(queries);
              }}
              onQueryOptionsChange={(queryOptions) => {
                setQueryOptions(queryOptions);
              }}
              onPluginSpecChange={(spec) => {
                pluginEditor.onSpecChange(spec);
              }}
              onLinksChange={(links) => {
                setLinks(links);
              }}
            />
          </VariableOpportunitiesProvider>
        </Container.Body>
      </Container>
    </ErrorBoundary>
  );

  const previewLayoutToggle = (
    <Stack spacing={1} direction="row" sx={{ alignItems: 'center', justifyContent: 'end' }}>
      {panelLayout === PanelLayoutOptions.WIDESCREEN && (
        <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
          Preview layout:
        </Typography>
      )}
      <Tooltip Content={panelLayout === PanelLayoutOptions.NARROW ? 'Preview layout' : ''}>
        <ToggleButtonGroup
          value={panelLayout}
          onChange={(_, value) => {
            analytics?.track('dashboard-toggled_panel_layout', { enabled: value });
            setPanelLayout(value);
          }}
        >
          <ToggleButton value={PanelLayoutOptions.WIDESCREEN} data-testid="widescreenToggle">
            <PageLayoutBody />
          </ToggleButton>
          <ToggleButton value={PanelLayoutOptions.NARROW} data-testid="narrowToggle">
            <PageLayoutSidebarRight />
          </ToggleButton>
        </ToggleButtonGroup>
      </Tooltip>
    </Stack>
  );

  const previewHeader = (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="h4">Preview</Typography>
      <Stack direction="row" spacing={2}>
        {previewLayoutToggle}
        <TimeRangeControls showTimeRangeWindowControls={true} showRefreshInterval={true} />
      </Stack>
    </Stack>
  );

  const variableInstances = group.splitVariableName ? (
    <Stack spacing={1} mb={1} alignItems="start">
      <Typography variant="h4">Variable instance</Typography>
      <SplitVariable.SelectVariable />
    </Stack>
  ) : null;

  const preview = (height?: number) => (
    <ErrorBoundary FallbackComponent={ErrorAlert}>
      <PanelPreview
        height={height}
        panelDefinition={panelDefinition}
        onDefinitionChange={handlePanelDefinitionChange}
      />
    </ErrorBoundary>
  );

  // Check if AI generation is available
  const isAIGenerationAvailable = PanelNameDescriptionGenerator && queryStrings.length > 0;

  // Helper to render name/description fields with optional AI wrapper
  const renderNameDescriptionFields = (narrowLayout: boolean, nameAI?: FieldAIProps, descriptionAI?: FieldAIProps) => {
    if (narrowLayout) {
      return (
        <>
          <Grid item xs={12}>
            {renderNameField(nameAI)}
          </Grid>
          <Grid item xs={12}>
            {renderDescriptionField(descriptionAI)}
          </Grid>
        </>
      );
    }
    return (
      <>
        <Grid item xs={group.kind === 'Grid' ? 8 : 12}>
          {renderNameField(nameAI)}
        </Grid>
        {group.kind === 'Grid' && (
          <Grid item xs={4}>
            {groupField}
          </Grid>
        )}
        <Grid item xs={8}>
          {renderDescriptionField(descriptionAI)}
        </Grid>
        <Grid item xs={4}>
          {typeField}
        </Grid>
      </>
    );
  };

  if (panelLayout === PanelLayoutOptions.NARROW) {
    return (
      <Stack component="form" id={panelEditorFormId}>
        <PanelProvider panelKey={panelKey} panelDefinition={panelDefinition} isEditPanel={true}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              backgroundColor: (theme) => theme.palette.background.container,
              flexGrow: 1,
              pt: 0.5,
              marginBottom: 1,
            }}
          >
            <Grid container spacing={2} data-testid={`${panelEditorFormId}-content`} sx={{ alignContent: 'start' }}>
              <Grid item xs={12}>
                <Typography variant="h4" sx={{ padding: (t) => t.spacing(1, 0, 0.5) }}>
                  Panel
                </Typography>
              </Grid>
              {isAIGenerationAvailable ? (
                <PanelNameDescriptionGenerator
                  queries={queryStrings}
                  queryTypes={queryTypes}
                  panelType={plugin.kind}
                  currentName={panelDefinition.spec.display.name ?? ''}
                  currentDescription={panelDefinition.spec.display.description ?? ''}
                  isNewPanel={!panelKey}
                  onGenerate={handleAIGenerate}
                >
                  {({ name, description }) => renderNameDescriptionFields(true, name, description)}
                </PanelNameDescriptionGenerator>
              ) : (
                renderNameDescriptionFields(true)
              )}
              {group.kind === 'Grid' && (
                <Grid item xs={12}>
                  {groupField}
                </Grid>
              )}
              <Grid item xs={12}>
                {typeField}
              </Grid>
            </Grid>
            <Grid
              container
              spacing={2}
              sx={{ alignContent: 'start', ['> .MuiGrid-item:first-child']: { paddingTop: 0 } }}
            >
              <Grid item xs={12}>
                {isRelativeTimeOverrideDefined ? (
                  <DisabledTimeRangeControls timeOverride={panelDefinition.spec.query_options?.time}>
                    {previewLayoutToggle}
                  </DisabledTimeRangeControls>
                ) : (
                  previewHeader
                )}
              </Grid>
              {/* Keeps nested TimeRangeProvider in sync when TimeOptionsEditor used to update query_options.time.start */}
              <TimeOverrideProvider panelDefinition={panelDefinition} checkQueryOptionDefined={false}>
                <Grid item xs={12}>
                  {variableInstances}
                  {preview(248)}
                </Grid>
              </TimeOverrideProvider>
            </Grid>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {editor}
            </Grid>
          </Grid>
        </PanelProvider>
      </Stack>
    );
  }

  return (
    <Box
      component="form"
      id={panelEditorFormId}
      sx={{
        backgroundColor: (theme) => theme.palette.background.container,
        flexGrow: 1,
        pt: 0.5,
      }}
    >
      <Grid container spacing={2} data-testid={`${panelEditorFormId}-content`}>
        {isAIGenerationAvailable ? (
          <PanelNameDescriptionGenerator
            queries={queryStrings}
            queryTypes={queryTypes}
            panelType={plugin.kind}
            currentName={panelDefinition.spec.display.name ?? ''}
            currentDescription={panelDefinition.spec.display.description ?? ''}
            isNewPanel={!panelKey}
            onGenerate={handleAIGenerate}
          >
            {({ name, description }) => renderNameDescriptionFields(false, name, description)}
          </PanelNameDescriptionGenerator>
        ) : (
          renderNameDescriptionFields(false)
        )}
        {isRelativeTimeOverrideDefined && (
          <Grid item xs={12}>
            <DisabledTimeRangeControls timeOverride={panelDefinition.spec.query_options?.time}>
              {previewLayoutToggle}
            </DisabledTimeRangeControls>
          </Grid>
        )}
        {/* Keeps nested TimeRangeProvider in sync when TimeOptionsEditor used to update query_options.time.start */}
        <TimeOverrideProvider panelDefinition={panelDefinition} checkQueryOptionDefined={false}>
          {!isRelativeTimeOverrideDefined && (
            <Grid item xs={12}>
              {previewHeader}
            </Grid>
          )}
          <PanelProvider panelKey={panelKey} panelDefinition={panelDefinition} isEditPanel={true}>
            <Grid item xs={12}>
              {variableInstances}
              {preview()}
            </Grid>
            <Grid item xs={12}>
              {editor}
            </Grid>
          </PanelProvider>
        </TimeOverrideProvider>
      </Grid>
    </Box>
  );
}

/**
 * The `id` attribute added to the `PanelEditorForm` component, allowing submit buttons to live outside the form.
 */
export const panelEditorFormId = 'panel-editor-form';
